import React, { useState, useEffect, useCallback } from 'react';

import { QuestionCircleOutlined, UploadOutlined, RobotOutlined, CodeOutlined } from '@ant-design/icons';
import PaperSizeFormItems from '@components/shared/templates/PaperSizeFormItems';
import TemplatePreviewModal from '@components/shared/templates/TemplatePreviewModal';
import TemplatePreviewSplitPane from '@components/shared/templates/TemplatePreviewSplitPane';
import { useSingleNotification } from '@hooks/admin/useBackgroundJobNotification';
import { useSingleNotification as useOperatorSingleNotification } from '@hooks/operator/useBackgroundJobNotification';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  message,
  Space,
  Select,
  Spin,
  Modal,
  Alert,
  Row,
  Col,
  Upload,
  Progress
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { createAssistantMessage, getConversation } from '@services/shared/aiAssistantService';

import AIAssistantPanel from './AIAssistantPanel';
import HTMLEditorDrawer from './HTMLEditorDrawer';
import LiquidVariablesDrawer from './LiquidVariablesDrawer';

import { getPaperSizeOptions, getActualPaperSize } from '@/constants/paperSizes';

const { TextArea } = Input;

// ===== Type definitions =====

type PaperSize = { width?: number | null; height?: number | null } | null | undefined;

type AssistantMessagePart = string | { type?: string; text?: string };

interface AssistantMessage {
  id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | string;
  content?: AssistantMessagePart[] | null;
}

interface PollAssistantOptions {
  intervalMs?: number;
  maxAttempts?: number;
}

interface SalesChannelOption {
  label?: string;
  value?: string;
  [key: string]: unknown;
}

// callback 只用來通知 wrapper「record 載入完成,把 context.currentMerchant 對齊到 record 的
// merchant」(見 useRecordMerchantSync.handleRecordLoaded),因此只宣告 merchant_id / merchant 兩個
// 欄位即可;不宜加 `[key: string]: unknown` index signature — TS 會把所有 access 視為 unknown,
// 反而打破與 RecordWithMerchant 等其他簽名的兼容(踩過 ci typecheck)。
interface OrderTemplateLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
}

interface OrderTemplatesEditProps {
  role?: 'admin' | 'operator';
  service: Record<string, any>;
  salesChannels?: SalesChannelOption[];
  onOrderTemplateLoaded?: (record: OrderTemplateLoadedPayload) => void;
  merchantDataLoading?: boolean;
}

// ===== AI Assistant helpers =====

// 把目前 form 上的模板 / 紙張尺寸 + 規則指示包進 user_query。
// 嵌在每則訊息，AI 永遠拿到最新狀態（即便對話中途模板被改過）。
const buildTemplateAssistantPrompt = (userMessage: string, templateContent: string, paperSize: PaperSize): string => {
  const widthMm = paperSize?.width ?? '?';
  const heightMm = paperSize?.height ?? '?';
  return [
    '你是訂單 Liquid 模板的版面助手。目前模板（HTML + Liquid）：',
    '```html',
    templateContent || '<empty>',
    '```',
    `紙張尺寸：${widthMm}mm x ${heightMm}mm。`,
    '',
    '規則：',
    '1. 若使用者要求調整模板，請回傳「完整」修改後的 HTML，包在單一 ```html ... ``` fenced code block 內。',
    '2. 必須保留 {{ reserved_barcode_area }} 變數，不可刪除。',
    '3. code block 之外只用「一句中文概述」說明改了什麼，最多 30 個字；不要解釋實作細節（用了什麼 CSS 選擇器、什麼技巧、為什麼這樣做、保留了哪些變數等都省略）。',
    '',
    `使用者請求：${userMessage}`,
  ].join('\n');
};

// 從 message.content（array of text / structured parts）抽出純文字
const extractMessageText = (message: AssistantMessage | null | undefined): string => {
  if (!message?.content) return '';
  return Array.from(message.content)
    .map((part: AssistantMessagePart) => {
      if (typeof part === 'string') return part;
      if (part && typeof part === 'object' && (part.type === 'text' || !part.type)) {
        return part.text ?? '';
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');
};

// 從文字抽第一個 ```html ... ``` fenced code block
const extractHtmlCodeBlock = (text: string | null | undefined): string | null => {
  if (!text) return null;
  const match = text.match(/```html\s*\n([\s\S]*?)```/i);
  return match ? match[1].trim() : null;
};

// 把 ```html ... ``` block 從文字裡剝掉（HTML 已套用到 form.content，chat
// 裡不需要再印一次冗長的模板原始碼，只留 AI 的中文說明就好）
const stripHtmlCodeBlock = (text: string | null | undefined): string => {
  if (!text) return '';
  return text
    .replace(/```html\s*\n[\s\S]*?```/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Poll conversation 直到目標 assistant message 變 completed
const pollAssistantMessage = async (
  conversationId: string,
  assistantMessageId: string,
  options: PollAssistantOptions = {}
): Promise<AssistantMessage> => {
  const { intervalMs = 1500, maxAttempts = 80 } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    const res = await getConversation(conversationId);
    if (!res.success) throw new Error('Failed to fetch conversation');

    const messages: AssistantMessage[] = res.data?.messages || [];
    const target = messages.find((m) => m.id === assistantMessageId);
    if (!target) continue;
    if (target.status === 'completed') return target;
    if (target.status === 'failed') throw new Error('AI run failed');
  }

  throw new Error('AI response timeout');
};

/**
 * OrderTemplatesEdit - Order Template edit/create form component
 *
 * Merchant-agnostic: salesChannels 由 wrapper 注入；loaded 後透過
 * onOrderTemplateLoaded 通知 wrapper 將 context 對齊到 record 的 merchant
 * （見 useRecordMerchantSync）。
 */
// AI 助手(GoBot) 與「上傳 PDF 自動生成」入口暫時隱藏（產品決策）。
// 相關 Drawer / Modal / handler 仍保留，改成 true 即可恢復這兩個入口。
const SHOW_AI_TOOLS = false;

const OrderTemplatesEdit: React.FC<OrderTemplatesEditProps> = ({
  role = 'admin',
  service,
  salesChannels = [],
  onOrderTemplateLoaded,
  merchantDataLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  // 新增模式採用兩步驟 wizard：step 0 = 基本資訊；step 1 = 內容 / 選項。編輯模式一頁顯示。
  const useWizard = isNew;
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [variablesDrawerVisible, setVariablesDrawerVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  // AI Assistant states：點 GoBot 後在同頁面 inline 顯示 chat panel（不再開 drawer）
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [gobotBtnHover, setGobotBtnHover] = useState(false);
  // 跨多則訊息維持同一 Ai::Conversation（後端記住歷史）
  const [aiConversationId, setAiConversationId] = useState<string | null>(null);

  // HTML Editor Drawer state
  const [htmlEditorVisible, setHtmlEditorVisible] = useState(false);

  // PDF AI Analysis states
  const [pdfAnalyzing, setPdfAnalyzing] = useState(false);
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [pdfProgressModalVisible, setPdfProgressModalVisible] = useState(false);
  const [pdfAnalysisProgress, setPdfAnalysisProgress] = useState({ current: 0, total: 100 });
  const [pdfAnalysisMessage, setPdfAnalysisMessage] = useState('');

  // PDF Upload Modal states
  const [pdfUploadModalVisible, setPdfUploadModalVisible] = useState(false);
  const [renderError, setRenderError] = useState<unknown>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<any>(null);
  const [pdfFileList, setPdfFileList] = useState<any[]>([]);

  // Background job notification hook (based on role)
  const jobNotificationHook = role === 'operator' ? useOperatorSingleNotification : useSingleNotification;

  // Wrap callbacks with useCallback to prevent re-subscription
  const handlePdfProgress = useCallback((data: any) => {
    console.log('[PDF Analysis] Progress:', data);
    if (data.progress) {
      setPdfAnalysisProgress(data.progress);
    }
    if (data.message) {
      setPdfAnalysisMessage(data.message);
    }
  }, []);

  const handlePdfCompleted = useCallback((data: any) => {
    console.log('[PDF Analysis] Completed:', data);

    // Fill the content field with the generated liquid template
    if (data.data && data.data.liquid_template) {
      form.setFieldsValue({ content: data.data.liquid_template });
      message.success(t('shared.order_templates.form.pdf_analysis_completed'));
    }

    setPdfProgressModalVisible(false);
    setPdfAnalyzing(false);
    setPdfJobId(null);
  }, [form, t]);

  const handlePdfFailed = useCallback((data: any) => {
    console.error('[PDF Analysis] Failed:', data);
    message.error(t('shared.order_templates.form.pdf_analysis_failed') + (data.error ? `: ${data.error}` : ''));
    setPdfProgressModalVisible(false);
    setPdfAnalyzing(false);
    setPdfJobId(null);
  }, [t]);

  // Listen to PDF analysis job progress
  // jobNotificationHook 在兩個 role 下型別略有差異，於此 cast 為 any 保留原行為。
  (jobNotificationHook as any)(pdfJobId, {
    onProgress: handlePdfProgress,
    onCompleted: handlePdfCompleted,
    onFailed: handlePdfFailed
  });

  // Split preview is now always visible (removed toggle)

  // Sample data with reserved_barcode_area loaded from API
  const [sampleOrderData, setSampleOrderData] = useState<any>(null);

  // Generic select filter function
  const filterOption = useCallback((input: string, option: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    []
  );

  // Watch form fields for preview
  const contentValue = Form.useWatch('content', form);
  const paperSizeType = Form.useWatch('paper_size_type', form);
  const paperWidth = Form.useWatch('paper_width', form);
  const paperHeight = Form.useWatch('paper_height', form);

  // Calculate actual paper size
  const actualPaperSize = getActualPaperSize(paperSizeType, paperWidth, paperHeight);

  useEffect(() => {
    loadData();
    loadSampleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, i18n.language]); // 當語系改變時也重新載入（新增模式會載入對應語系的預設模板）

  const loadSampleData = async () => {
    try {
      // i18n.language returns the current language code ('zh' or 'en')
      const locale = i18n.language || 'zh';

      // Import the module
      const { getSampleOrderData } = await import('@/constants/liquidTemplateData');

      // Get base data by locale (without reserved_barcode_area)
      const baseData = getSampleOrderData(locale);

      // Fetch reserved_barcode_area from API
      const response = await service.getSampleReservedBarcodeArea(locale);

      if (response.success && response.data && response.data.html) {
        setSampleOrderData({
          ...baseData,
          reserved_barcode_area: response.data.html
        });
      } else {
        console.warn('[loadSampleData] Failed to load reserved_barcode_area');
        setSampleOrderData(baseData);
      }
    } catch (error) {
      console.error('Failed to load sample order data:', error);
      // Fallback to default data
      const { SAMPLE_ORDER_DATA } = await import('@/constants/liquidTemplateData');
      setSampleOrderData(SAMPLE_ORDER_DATA);
    }
  };

  // 新增模式：載入預設模板內容並套用到 form（抽成 helper 以便重置後重新套用）
  const applyDefaultsForNew = async () => {
    try {
      const locale = i18n.language || 'zh';
      const defaultTemplateResponse = await service.getDefaultTemplate(locale);
      if (defaultTemplateResponse.success) {
        form.setFieldsValue({
          content: defaultTemplateResponse.data.content,
          paper_size_type: 'a4',
        });
      } else {
        // Fallback to empty content if fetching default template fails
        form.setFieldsValue({
          content: '',
          paper_size_type: 'a4',
        });
      }
    } catch (error) {
      console.error('Failed to load default template:', error);
      // Fallback to empty content
      form.setFieldsValue({
        content: '',
        paper_size_type: 'a4',
      });
    }
  };

  const loadData = async () => {
    try {
      setDataLoading(true);

      // Load order template data if editing
      if (id) {
        const response = await service.getById(id);
        if (response.success) {
          const orderTemplate = response.data;

          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
          // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onOrderTemplateLoaded?.(orderTemplate);

          form.setFieldsValue({
            name: orderTemplate.name,
            sales_channel_ids: orderTemplate.sales_channel_ids || [],
            content: orderTemplate.content,
            description: orderTemplate.description,
            paper_size_type: orderTemplate.paper_size_type || 'a4',
            paper_width: orderTemplate.paper_width,
            paper_height: orderTemplate.paper_height,
          });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') });
          navigate('/order_templates');
        }
      } else {
        // New template: fetch and initialize with default template
        await applyDefaultsForNew();
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // 預覽模板
  const handlePreview = () => {
    const content = form.getFieldValue('content');

    if (!content) {
      message.warning(t('common.validation.required'));
      return;
    }

    if (!sampleOrderData) {
      message.warning('Sample data is loading, please try again...');
      return;
    }

    setPreviewVisible(true);
  };

  // 組出 API payload + 前端驗證；驗證失敗回傳 null
  const buildProcessedValues = (values: Record<string, any>) => {
    // 前端驗證：檢查是否包含 {{reserved_barcode_area}}（允許有無空白）
    const content = values.content || '';
    const hasReservedBarcodeArea = /\{\{\s*reserved_barcode_area\s*\}\}/.test(content);

    if (!hasReservedBarcodeArea) {
      message.error(t('shared.order_templates.form.missing_reserved_barcode_area'));
      return null;
    }

    return {
      name: values.name,
      sales_channel_ids: values.sales_channel_ids || [],
      content: values.content,
      description: values.description,
      paper_size_type: values.paper_size_type || 'a4',
      paper_width: values.paper_size_type === 'custom' ? values.paper_width : null,
      paper_height: values.paper_size_type === 'custom' ? values.paper_height : null,
    };
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response
  // Note: For operator role, merchant_id is automatically injected by useOperatorOrderTemplateService hook
  const submitValues = async (processedValues: Record<string, any>) => {
    return id
      ? await service.update(id, processedValues)
      : await service.create(processedValues);
  };

  // 進入下一步：先驗證 step 0 的欄位，通過才前進
  const handleNext = async () => {
    try {
      await form.validateFields(['name', 'sales_channel_ids', 'paper_size_type', 'paper_width', 'paper_height']);
      setCurrentStep(1);
    } catch {
      // antd 已自動高亮錯誤欄位
    }
  };

  const handlePrev = () => {
    setCurrentStep(0);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values: Record<string, any>) => {
    // wizard 模式下，僅允許在 step 1 觸發提交（避免在 step 0 按 Enter 誤送）
    if (useWizard && currentStep !== 1) return;

    const processedValues = buildProcessedValues(values);
    if (!processedValues) return;

    setLoading(true);
    try {
      const response = await submitValues(processedValues);

      if (response.success) {
        message.success(t(id ? `common.message.save_success` : `common.message.save_success`));
        // 導向該筆記錄的 show 頁
        const recordId = response.data?.id || id;
        navigate(`/order_templates/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(id ? `common.error.save_failed` : `common.error.save_failed`)
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const processedValues = buildProcessedValues(values);
      if (!processedValues) return;

      setSavingAndAdding(true);
      try {
        const response = await submitValues(processedValues);

        if (response.success) {
          message.success(t('common.message.save_success'));
          // 重置表單並重新套用預設模板內容
          form.resetFields();
          await applyDefaultsForNew();
          // wizard 回到第一步，方便連續建立
          setCurrentStep(0);
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.save_failed')
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // Open PDF upload modal
  const handleOpenPdfModal = () => {
    setPdfUploadModalVisible(true);
    setSelectedPdfFile(null);
    setPdfFileList([]);
  };

  // Handle PDF file selection
  const handlePdfFileChange = ({ file, fileList }: { file: any; fileList: any[] }) => {
    // Validate file type
    if (file.type !== 'application/pdf') {
      message.error(t('shared.order_templates.form.invalid_pdf_file'));
      return;
    }

    // Only keep the latest file
    setSelectedPdfFile(file);
    setPdfFileList(fileList.slice(-1));
  };

  // Handle PDF file removal
  const handlePdfFileRemove = () => {
    setSelectedPdfFile(null);
    setPdfFileList([]);
  };

  // Confirm and start PDF AI analysis
  const handleConfirmPdfUpload = async () => {
    if (!selectedPdfFile) {
      message.warning(t('shared.order_templates.form.select_pdf_first'));
      return;
    }

    // Close upload modal
    setPdfUploadModalVisible(false);

    // Start analysis
    setPdfAnalyzing(true);
    setPdfProgressModalVisible(true);
    setPdfAnalysisProgress({ current: 0, total: 100 });
    setPdfAnalysisMessage(t('background_job.workers.pdf_ai_analysis.start'));

    try {
      const formData = new FormData();
      formData.append('file', selectedPdfFile);

      const response = await service.analyzePdf(formData);

      if (response.success && response.data.job_id) {
        setPdfJobId(response.data.job_id);
        console.log('[PDF Analysis] Job started:', response.data.job_id);
      } else {
        handleApiError(response, { t, fallbackMessage: t('shared.order_templates.form.pdf_analysis_failed') });
        setPdfProgressModalVisible(false);
        setPdfAnalyzing(false);
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('PDF upload error:', error);
      setPdfProgressModalVisible(false);
      setPdfAnalyzing(false);
    }
  };

  // Handle AI Assistant message —— 走 /api/ai/v1/assistant + polling pattern。
  // Python AI 是 fire-and-forget；後端立刻回 IDs，FE 自行 polling conversation 取結果。
  const handleAiSendMessage = async (userMessage: string) => {
    setAiProcessing(true);

    try {
      // 把當下 form 上的 template 內容 + 紙張尺寸塞進 user_query。
      // 持續嵌入，AI 永遠看到最新狀態（即便對話中途使用者手動改過模板）。
      const query = buildTemplateAssistantPrompt(
        userMessage,
        form.getFieldValue('content'),
        actualPaperSize
      );

      const createRes = await createAssistantMessage({
        message: query,
        conversationId: aiConversationId,
        assistantType: 'template_assistant',
      });

      if (!createRes.success) {
        handleApiError(createRes, { t, fallbackMessage: t('shared.order_templates.ai_assistant.error') });
        throw new Error(createRes.message || 'AI assistant create failed');
      }

      const newConvId = createRes.data?.conversation_id;
      const assistantMsgId = createRes.data?.assistant_message_id;
      if (!newConvId || !assistantMsgId) {
        throw new Error('AI assistant create: missing ids in response');
      }
      if (!aiConversationId) setAiConversationId(newConvId);

      const completedMsg = await pollAssistantMessage(newConvId, assistantMsgId);
      const rawText = extractMessageText(completedMsg);

      // 從回覆抽 ```html ... ``` fenced code block，找到就把整段塞回 form.content
      const modifiedTemplate = extractHtmlCodeBlock(rawText);
      if (modifiedTemplate) {
        form.setFieldsValue({ content: modifiedTemplate });
      }

      // chat 裡不印 html block（已套用到左欄 form）— 只留 AI 的中文說明
      return stripHtmlCodeBlock(rawText);
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('AI Assistant error:', error);
      throw error;
    } finally {
      setAiProcessing(false);
    }
  };

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? ['orders', 'orders.templates', { label: id ? t('common.button.edit') : t('common.button.create') }]
    : ['orders', 'orders.templates', { label: id ? t('common.button.edit') : t('common.button.create') }];

  // Render-gate：等到 wrapper 把 currentMerchant 切到 record 的 merchant，才渲染 form，
  // 避免閃過上一個 merchant 的下拉資料。dataLoading 同上。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={breadcrumbItems}
      />
      <Row gutter={24}>
        {/* Left: Form (or inline AI panel when GoBot clicked) */}
        <Col span={8}>
          <div style={{ display: aiPanelOpen ? 'none' : 'block' }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              {/* Step 0：基本資訊（模板名稱 / 通路 / 紙張尺寸） */}
              <div style={{ display: useWizard && currentStep !== 0 ? 'none' : 'block' }}>
                <Form.Item
                  label={t(`models.order_template.name`)}
                  name="name"
                  rules={[{ required: true, message: t(`common.validation.required`) }]}
                >
                  <Input placeholder={t(`common.placeholder.please_enter`)} />
                </Form.Item>

                <Form.Item
                  label={t(`models.order_template.sales_channel_ids`)}
                  name="sales_channel_ids"
                  rules={[{ required: true, message: t(`common.validation.required`) }]}
                  tooltip={t(`shared.order_templates.form.sales_channel_ids_hint`)}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder={t(`common.placeholder.please_select`)}
                    options={salesChannels}
                    filterOption={filterOption}
                  />
                </Form.Item>

                {/* Paper Size Form Items (Shared Component) */}
                <PaperSizeFormItems
                  paperSizeOptions={getPaperSizeOptions(t)}
                  showCustomInputs={paperSizeType === 'custom'}
                  t={t}
                  modelName="order_template"
                  namespace="shared.order_templates"
                />
              </div>

              {/* Step 1：內容 / 選項（AI / HTML / PDF / 預設 / 描述） */}
              <div style={{ display: useWizard && currentStep !== 1 ? 'none' : 'block' }}>
                {/* AI Assistant and Tools Section */}
                <Form.Item>
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Primary Action: AI Assistant — 點下去切換到 inline panel */}
                    <Button
                      icon={<RobotOutlined />}
                      size="large"
                      block
                      onClick={() => setAiPanelOpen(true)}
                      onMouseEnter={() => setGobotBtnHover(true)}
                      onMouseLeave={() => setGobotBtnHover(false)}
                      style={{
                        height: '56px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        background: gobotBtnHover ? '#000' : '#ededed',
                        borderColor: gobotBtnHover ? '#000' : '#ededed',
                        color: gobotBtnHover ? '#fff' : '#000',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease'
                      }}
                    >
                      {t('shared.order_templates.form.ai_assistant')}
                    </Button>

                    {/* Secondary Actions */}
                    <Space style={{ width: '100%' }} size="small">
                      <Button
                        icon={<CodeOutlined />}
                        onClick={() => setHtmlEditorVisible(true)}
                        style={{ flex: 1 }}
                      >
                        {t('shared.order_templates.form.edit_html')}
                      </Button>
                      <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setVariablesDrawerVisible(true)}
                        style={{ flex: 1 }}
                      >
                        {t('shared.order_templates.form.view_variables')}
                      </Button>
                    </Space>

                    {/* 上傳 PDF 自動生成入口：暫時隱藏（產品決策），改 SHOW_AI_TOOLS = true 即恢復 */}
                    {SHOW_AI_TOOLS && (
                      <Button
                        type="dashed"
                        icon={<RobotOutlined />}
                        block
                        onClick={handleOpenPdfModal}
                        disabled={pdfAnalyzing}
                      >
                        {t('shared.order_templates.form.analyze_pdf')}
                      </Button>
                    )}
                  </Space>
                </Form.Item>

                {/* Hidden field for content (managed by drawers) */}
                <Form.Item
                  name="content"
                  rules={[{ required: true, message: t(`common.validation.required`) }]}
                  style={{ display: 'none' }}
                >
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item
                  label={t(`models.order_template.description`)}
                  name="description"
                >
                  <TextArea
                    rows={3}
                    placeholder={t(`common.placeholder.please_enter`)}
                  />
                </Form.Item>
              </div>

              <Form.Item>
                {useWizard && currentStep === 0 ? (
                  <Space>
                    <Button onClick={goBack}>
                      {t('common.button.cancel')}
                    </Button>
                    <Button type="primary" onClick={handleNext}>
                      {t('common.button.next')}
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    {useWizard && (
                      <Button onClick={handlePrev}>
                        {t('common.button.previous')}
                      </Button>
                    )}
                    <Button onClick={goBack}>
                      {t('common.button.cancel')}
                    </Button>
                    {isNew && (
                      <Button
                        loading={savingAndAdding}
                        disabled={loading || !!renderError}
                        onClick={handleSaveAndAddAnother}
                      >
                        {t('common.button.save_and_add_another')}
                      </Button>
                    )}
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      disabled={savingAndAdding || !!renderError}
                    >
                      {t('common.button.save')}
                    </Button>
                  </Space>
                )}
              </Form.Item>
            </Form>
          </div>

          {/* Inline AI Assistant Panel — 取代原本的右側 drawer */}
          <div style={{ display: aiPanelOpen ? 'block' : 'none' }}>
            <AIAssistantPanel
              paperSize={actualPaperSize}
              processing={aiProcessing}
              onSendMessage={handleAiSendMessage}
              onClose={() => setAiPanelOpen(false)}
            />
          </div>
        </Col>

        {/* Right: Preview (Always Visible, Larger) */}
        <Col span={16}>
          <TemplatePreviewSplitPane
            visible={true}
            content={contentValue}
            sampleData={sampleOrderData}
            paperSize={actualPaperSize}
            cssVariablePrefix="paper"
            t={t}
            namespace="shared.order_templates"
            onRenderError={setRenderError}
          />
        </Col>
      </Row>

      {/* HTML Editor Drawer */}
      <HTMLEditorDrawer
        visible={htmlEditorVisible}
        onClose={() => setHtmlEditorVisible(false)}
        value={contentValue}
        onChange={(newValue: string) => form.setFieldsValue({ content: newValue })}
      />

      {/* Liquid 變數說明 Drawer */}
      <LiquidVariablesDrawer
        visible={variablesDrawerVisible}
        onClose={() => setVariablesDrawerVisible(false)}
      />

      {/* Preview Modal (Shared Component) */}
      <TemplatePreviewModal
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        content={contentValue}
        sampleData={sampleOrderData}
        paperSize={actualPaperSize}
        cssVariablePrefix="paper"
        t={t}
        namespace="shared.order_templates"
      />

      {/* PDF Upload Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            {t('shared.order_templates.form.analyze_pdf')}
          </Space>
        }
        open={pdfUploadModalVisible}
        onCancel={() => setPdfUploadModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setPdfUploadModalVisible(false)}>
            {t('common.button.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleConfirmPdfUpload}
            disabled={!selectedPdfFile}
            icon={<RobotOutlined />}
          >
            {t('shared.order_templates.form.confirm_analyze')}
          </Button>
        ]}
      >
        <div style={{ padding: '20px 0' }}>
          <Alert
            message={t('shared.order_templates.form.pdf_upload_notice')}
            description={t('shared.order_templates.form.pdf_upload_description')}
            type="info"
            showIcon
            style={{ marginBottom: '20px' }}
          />

          <Upload.Dragger
            accept=".pdf"
            fileList={pdfFileList}
            beforeUpload={() => false}
            onChange={handlePdfFileChange}
            onRemove={handlePdfFileRemove}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              {t('shared.order_templates.form.upload_drag_text')}
            </p>
            <p className="ant-upload-hint">
              {t('shared.order_templates.form.upload_hint')}
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* PDF AI Analysis Progress Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            {t('shared.order_templates.form.pdf_analysis_progress')}
          </Space>
        }
        open={pdfProgressModalVisible}
        closable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <Progress
            percent={Math.round((pdfAnalysisProgress.current / pdfAnalysisProgress.total) * 100)}
            status="active"
          />
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#666' }}>
            {pdfAnalysisMessage && t(pdfAnalysisMessage)}
          </div>
          <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
            {pdfAnalysisProgress.current} / {pdfAnalysisProgress.total}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderTemplatesEdit;
